import { doc, getDoc, setDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorText, FieldLabel, LoadingView, PrimaryButton, SBCard, SecondaryButton } from "@/components/ui";
import { useAppEnvironmentReport } from "@/features/ai/AppEnvironmentScreen";
import type { SBotAppEnvironmentSurfaceReport } from "@/features/ai/services/appEnvironmentContext";
import { db, storage } from "@/firebase/app";
import { useDoc } from "@/hooks/firestoreHooks";
import { DEFAULT_PRIMARY_HEX, DEFAULT_SECONDARY_HEX, isValidHex, normalizedHex } from "@/models/appearance";
import { parseLeague } from "@/models/catalog";
import { organizationFirestoreData, parseOrganization, type OrganizationModel } from "@/models/organization";
import { isMembershipCurrentlyActive } from "@/models/user";
import { humanActionAuditActor, recordActionAudit } from "@/services/actionAudit";
import { useActiveLeagueID, useLeagueSnapshot } from "@/features/home/useLeagueSnapshot";
import { customOrganization } from "@/services/onboarding";
import "@/features/home/home.css";

// Ports of OrganizationListView drill-in + OrganizationAppearanceView:
// create organizations in the active league, edit branding and rosters.

function ColorField({ label, value, onChange, fallback }: { label: string; value: string; onChange: (v: string) => void; fallback: string }) {
  return (
    <FieldLabel label={label}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="color"
          value={isValidHex(value) ? `#${normalizedHex(value)}` : fallback}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 44, padding: 2 }}
        />
        <input value={value} placeholder={fallback} onChange={(e) => onChange(e.target.value)} />
      </div>
    </FieldLabel>
  );
}

export function OrganizationCreate() {
  const { currentUser } = useAuth();
  const { leagueID } = useActiveLeagueID();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [abbreviation, setAbbreviation] = useState("");
  const [location, setLocation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const create = async () => {
    if (!currentUser || !leagueID || !name.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      const leagueSnapshot = await getDoc(doc(db, "leagues", leagueID));
      const league = leagueSnapshot.exists() ? parseLeague(leagueSnapshot.id, leagueSnapshot.data()) : null;
      if (!league) throw new Error("Active league could not be loaded.");
      const organization = customOrganization(league.sportID, leagueID, name, abbreviation, location, currentUser.id);
      organization.adminUserIDs = [currentUser.id];
      await setDoc(doc(db, "organizations", organization.id), organizationFirestoreData(organization));
      navigate(`/organizations/${organization.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Organization could not be created.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="list-screen">
      <div className="list-screen-head">
        <h1>New Organization</h1>
      </div>
      <SBCard className="home-card">
        <FieldLabel label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="SBot Racing" />
        </FieldLabel>
        <div style={{ display: "flex", gap: 10 }}>
          <FieldLabel label="Abbreviation">
            <input value={abbreviation} onChange={(e) => setAbbreviation(e.target.value)} />
          </FieldLabel>
          <FieldLabel label="Location">
            <input value={location} onChange={(e) => setLocation(e.target.value)} />
          </FieldLabel>
        </div>
        <PrimaryButton title={isSaving ? "Creating…" : "Create organization"} onClick={() => void create()} disabled={isSaving || !name.trim() || !leagueID} />
        <ErrorText>{error}</ErrorText>
      </SBCard>
    </div>
  );
}

type RosterKey = "adminUserIDs" | "managerUserIDs" | "staffUserIDs" | "coachUserIDs" | "athleteUserIDs";

export function OrganizationDetail() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { currentUser, isSBotLabsAdmin } = useAuth();
  const snapshot = useLeagueSnapshot(false);

  const organization = useDoc(organizationId ? doc(db, "organizations", organizationId) : null, parseOrganization);

  const [name, setName] = useState("");
  const [abbreviation, setAbbreviation] = useState("");
  const [location, setLocation] = useState("");
  const [primaryColor, setPrimaryColor] = useState("");
  const [secondaryColor, setSecondaryColor] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [loadedID, setLoadedID] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [rosterSelection, setRosterSelection] = useState("");
  const [rosterRole, setRosterRole] = useState<RosterKey>("athleteUserIDs");

  useEffect(() => {
    if (organization.data && organization.data.id !== loadedID) {
      setName(organization.data.name);
      setAbbreviation(organization.data.abbreviation ?? "");
      setLocation(organization.data.location ?? "");
      setPrimaryColor(organization.data.primaryColorHex ?? "");
      setSecondaryColor(organization.data.secondaryColorHex ?? "");
      setLoadedID(organization.data.id);
    }
  }, [organization.data, loadedID]);

  const canEdit =
    isSBotLabsAdmin ||
    (currentUser && organization.data
      ? organization.data.adminUserIDs.includes(currentUser.id) ||
        currentUser.memberships.some(
          (m) =>
            isMembershipCurrentlyActive(m) &&
            m.targetType === "league" &&
            m.role === "admin" &&
            m.targetID === organization.data?.leagueID,
        )
      : false);

  const environmentReport = useMemo<SBotAppEnvironmentSurfaceReport>(() => {
    const title = organization.data?.name ?? "Organization";
    return {
      identity: "organization-detail",
      owningTab: "home",
      breadcrumbs: ["Home", "Organizations", title],
      destinationType: "organization",
      entityDisplayName: organization.data?.name,
      surfaceLevel: "leaf",
      surfaceDepth: 2,
      surfacePresentation: "route",
      screen: {
        title,
        state: organization.error
          ? "error"
          : organization.loading
            ? "loading"
            : !organization.data
              ? "unavailable"
              : canEdit
                ? "form; unsaved values undisclosed"
                : "visible",
        contextCompleteness: "basic",
        sections: [{
          id: "organization-current-view",
          title: "Current View",
          controls: [{
            role: "status",
            label: organization.data?.name ?? "Organization",
            value: organization.data?.reviewStatus,
          }],
        }],
      },
    };
  }, [canEdit, organization.data, organization.error, organization.loading]);
  useAppEnvironmentReport(environmentReport);

  const membersByUserID = useMemo(() => {
    const map = new Map(snapshot.memberSummaries.map((member) => [member.userID, member]));
    return map;
  }, [snapshot.memberSummaries]);

  const saveOrganization = async (mutate: (org: OrganizationModel) => OrganizationModel) => {
    if (!organization.data) return;
    setIsSaving(true);
    setError(null);
    setMessage(null);
    try {
      const next = mutate({ ...organization.data, updatedAt: new Date() });
      await setDoc(doc(db, "organizations", next.id), organizationFirestoreData(next));
      if (currentUser) {
        void recordActionAudit({
          before: organizationFirestoreData(organization.data),
          after: organizationFirestoreData(next),
          context: {
            leagueID: next.leagueID,
            organizationID: next.id,
            targetCollection: "organizations",
            targetDocumentID: next.id,
            targetDisplayName: next.name,
            actionType: "update",
            actor: humanActionAuditActor(currentUser),
          },
        });
      }
      setMessage("Saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setIsSaving(false);
    }
  };

  const saveDetails = async () => {
    if ((primaryColor && !isValidHex(primaryColor)) || (secondaryColor && !isValidHex(secondaryColor))) {
      setError("Colors must be 6-digit hex values.");
      return;
    }
    let logoURL: string | undefined;
    if (logoFile && organization.data) {
      const extension = logoFile.name.split(".").pop() || "png";
      const logoRef = ref(storage, `organizations/${organization.data.id}/logo-${Date.now()}.${extension}`);
      await uploadBytes(logoRef, logoFile);
      logoURL = await getDownloadURL(logoRef);
    }
    await saveOrganization((org) => ({
      ...org,
      name: name.trim() || org.name,
      abbreviation: abbreviation.trim() || undefined,
      location: location.trim() || undefined,
      primaryColorHex: primaryColor ? normalizedHex(primaryColor) : undefined,
      secondaryColorHex: secondaryColor ? normalizedHex(secondaryColor) : undefined,
      logoURL: logoURL ?? org.logoURL,
    }));
    setLogoFile(null);
  };

  const modifyRoster = async (key: RosterKey, userID: string, add: boolean) => {
    await saveOrganization((org) => {
      const current = org[key];
      const nextList = add ? (current.includes(userID) ? current : [...current, userID]) : current.filter((id) => id !== userID);
      if (key === "adminUserIDs" && nextList.length === 0) {
        throw new Error("Organizations need at least one admin.");
      }
      return { ...org, [key]: nextList };
    });
  };

  if (organization.loading && !organization.data) return <LoadingView />;
  if (organization.error) return <ErrorText>{organization.error}</ErrorText>;
  if (!organization.data) return <ErrorText>Organization not found.</ErrorText>;

  const org = organization.data;
  const rosterSections: { key: RosterKey; title: string }[] = [
    { key: "adminUserIDs", title: "Admins" },
    { key: "managerUserIDs", title: "Managers" },
    { key: "staffUserIDs", title: "Staff" },
    { key: "coachUserIDs", title: "Coaches" },
    { key: "athleteUserIDs", title: "Athletes" },
  ];

  return (
    <div className="list-screen">
      <div className="list-screen-head">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {org.logoURL && <img src={org.logoURL} alt="" style={{ width: 46, height: 46, borderRadius: 12, objectFit: "cover" }} />}
          <div>
            <h1>{org.name}</h1>
            <p className="home-hint">{[org.abbreviation, org.location].filter(Boolean).join(" · ")}</p>
          </div>
        </div>
        <span className="badge">{org.reviewStatus}</span>
      </div>

      {canEdit && (
        <SBCard className="home-card">
          <h3>Details & appearance</h3>
          <FieldLabel label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </FieldLabel>
          <div style={{ display: "flex", gap: 10 }}>
            <FieldLabel label="Abbreviation">
              <input value={abbreviation} onChange={(e) => setAbbreviation(e.target.value)} />
            </FieldLabel>
            <FieldLabel label="Location">
              <input value={location} onChange={(e) => setLocation(e.target.value)} />
            </FieldLabel>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <ColorField label="Primary color" value={primaryColor} onChange={setPrimaryColor} fallback={`#${DEFAULT_PRIMARY_HEX}`} />
            <ColorField label="Secondary color" value={secondaryColor} onChange={setSecondaryColor} fallback={`#${DEFAULT_SECONDARY_HEX}`} />
          </div>
          <FieldLabel label="Logo">
            <input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)} />
          </FieldLabel>
          <PrimaryButton title={isSaving ? "Saving…" : "Save"} onClick={() => void saveDetails()} disabled={isSaving} />
          <ErrorText>{error}</ErrorText>
          {message && <p className="home-hint">{message}</p>}
        </SBCard>
      )}

      {rosterSections.map((section) => {
        const ids = org[section.key];
        return (
          <SBCard key={section.key} className="home-card">
            <div className="home-card-head">
              <h3>{section.title}</h3>
              <span className="badge">{ids.length}</span>
            </div>
            {ids.map((userID) => {
              const member = membersByUserID.get(userID);
              return (
                <div key={userID} className="person-row">
                  <div className="person-main">
                    <strong>{member?.displayName ?? userID}</strong>
                    <span>{member ? member.roles.join(", ") : "Not in league member summaries"}</span>
                  </div>
                  {canEdit && (
                    <div className="person-roles">
                      <SecondaryButton title="Remove" destructive onClick={() => void modifyRoster(section.key, userID, false)} />
                    </div>
                  )}
                </div>
              );
            })}
            {ids.length === 0 && <p className="home-hint">Empty.</p>}
          </SBCard>
        );
      })}

      {canEdit && (
        <SBCard className="home-card">
          <h3>Add league member to roster</h3>
          <div style={{ display: "flex", gap: 10 }}>
            <select value={rosterSelection} onChange={(e) => setRosterSelection(e.target.value)} style={{ flex: 2 }}>
              <option value="">— Choose member —</option>
              {snapshot.memberSummaries.map((member) => (
                <option key={member.userID} value={member.userID}>
                  {member.displayName}
                </option>
              ))}
            </select>
            <select value={rosterRole} onChange={(e) => setRosterRole(e.target.value as RosterKey)} style={{ flex: 1 }}>
              <option value="athleteUserIDs">Athlete</option>
              <option value="coachUserIDs">Coach</option>
              <option value="staffUserIDs">Staff</option>
              <option value="managerUserIDs">Manager</option>
              <option value="adminUserIDs">Admin</option>
            </select>
            <SecondaryButton
              title="Add"
              disabled={!rosterSelection}
              onClick={() => rosterSelection && void modifyRoster(rosterRole, rosterSelection, true)}
            />
          </div>
        </SBCard>
      )}
    </div>
  );
}
